import { FC, ReactNode, useEffect } from 'react';

import { Footer, Header, UserCentric } from '@catawiki/layout';

import { isInMobileApp } from '@/shared/utils/isInMobileApp';
import { usePagesRuntimeConfig } from '@/shared/utils/runtimeConfig';

import {
  LayoutAssets,
  LayoutHeadTags,
  LayoutJsTags,
  PagesTheme,
} from './LayoutData';
import { HeaderSettings } from '../models/header';
import { isInBuilder } from '../utils/isInBuilder';
import { MarketingHeader } from './headers/MarketingHeader';
import { PartnershipHeader } from './headers/PartnershipHeader';

interface Props extends HeaderSettings {
  assetsData?: LayoutAssets;
  children: ReactNode;
}

export const Layout: FC<Props> = ({
  assetsData,
  theme,
  partnershipLogo,
  stickyHeader,
  children,
}) => {
  const inApp = isInMobileApp();
  const insideBuilder = isInBuilder();
  const runtimeConfig = usePagesRuntimeConfig();
  const runtimeEnvironment = runtimeConfig.stage ?? runtimeConfig.environment;
  const environment = runtimeEnvironment ?? 'production';

  useEffect(() => {
    if (inApp) {
      document.body.classList.remove('tw:header-offset');
    }
  }, [inApp]);

  if (!assetsData) return <div>{children}</div>;

  const header = (() => {
    switch (theme) {
      case PagesTheme.MARKETING:
        return (
          <MarketingHeader
            assetsData={assetsData}
            stickyHeader={stickyHeader}
          />
        );
      case PagesTheme.PARTNERSHIP:
        return (
          <PartnershipHeader
            assetsData={assetsData}
            partnershipLogo={partnershipLogo}
            stickyHeader={stickyHeader}
          />
        );
      case PagesTheme.FOCUSED:
        return <Header environment={environment} variant="focused" />;
      default:
        return <Header environment={environment} variant="buyer" />;
    }
  })();

  return (
    <div className="u-typography-body">
      <LayoutHeadTags assetsData={assetsData} />
      {inApp ? null : header}
      {children}
      {inApp ? null : <Footer />}
      {inApp || insideBuilder || runtimeEnvironment !== 'production' ? null : (
        <UserCentric />
      )}
      <LayoutJsTags assetsData={assetsData} />
    </div>
  );
};
